import { useState, useEffect } from "react";
import axios from "axios";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Bell, RefreshCw, Settings, ExternalLink, TrendingUp, AlertCircle, CheckCircle2, Clock, FileText, Globe, Scale } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LegalWatch({ user, cabinet, onLogout }) {
  const [updates, setUpdates] = useState([]);
  const [domains, setDomains] = useState([]);
  const [preferences, setPreferences] = useState({ tracked_domains: [], notification_enabled: true });
  const [loading, setLoading] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState("all");
  const [selectedJurisdiction, setSelectedJurisdiction] = useState("all");
  const [preferencesDialogOpen, setPreferencesDialogOpen] = useState(false);
  const [selectedUpdate, setSelectedUpdate] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [sortBy, setSortBy] = useState("date_desc");

  useEffect(() => {
    loadDomains();
    loadPreferences();
    loadUpdates();
  }, []);

  const loadDomains = async () => {
    try {
      const token = localStorage.getItem('lawliet_token');
      const response = await axios.get(`${API}/legal-watch/domains`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDomains(response.data.domains || []);
    } catch (error) {
      console.error("Error loading domains:", error);
    }
  };

  const loadPreferences = async () => {
    try {
      const token = localStorage.getItem('lawliet_token');
      const response = await axios.get(`${API}/legal-watch/preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreferences(response.data);
    } catch (error) {
      console.error("Error loading preferences:", error);
    }
  };

  const loadUpdates = async (domain = null, jurisdiction = null) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('lawliet_token');
      
      // Build URL with filters
      let url = `${API}/legal-watch/updates?`;
      if (domain && domain !== "all") {
        url += `domain=${domain}&`;
      }
      if (jurisdiction && jurisdiction !== "all") {
        url += `jurisdiction=${jurisdiction}&`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setUpdates(response.data.updates || []);
    } catch (error) {
      toast.error("Erreur lors du chargement des mises à jour");
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = async () => {
    try {
      const token = localStorage.getItem('lawliet_token');
      await axios.post(
        `${API}/legal-watch/preferences`,
        preferences,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Préférences enregistrées");
      setPreferencesDialogOpen(false);
      loadUpdates();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const toggleDomain = (domainId) => {
    setPreferences(prev => {
      const tracked = prev.tracked_domains.includes(domainId)
        ? prev.tracked_domains.filter(d => d !== domainId)
        : [...prev.tracked_domains, domainId];
      return { ...prev, tracked_domains: tracked };
    });
  };

  const handleRefresh = () => {
    loadUpdates(selectedDomain, selectedJurisdiction);
    toast.success("Vérification des mises à jour...");
  };

  const handleDomainFilter = (domainId) => {
    setSelectedDomain(domainId);
    loadUpdates(domainId === "all" ? null : domainId, selectedJurisdiction === "all" ? null : selectedJurisdiction);
  };

  const handleJurisdictionFilter = (jurisdictionId) => {
    setSelectedJurisdiction(jurisdictionId);
    loadUpdates(selectedDomain === "all" ? null : selectedDomain, jurisdictionId === "all" ? null : jurisdictionId);
  };

  const getImpactBadgeColor = (level) => {
    switch (level) {
      case "high":
        return "bg-red-100 text-red-800";
      case "medium":
        return "bg-amber-100 text-amber-800";
      case "low":
        return "bg-green-100 text-green-800";
      default:
        return "bg-slate-100 text-slate-800";
    }
  };

  const getImpactIcon = (level) => {
    switch (level) {
      case "high":
        return <AlertCircle className="h-4 w-4" />;
      case "medium":
        return <TrendingUp className="h-4 w-4" />;
      case "low":
        return <CheckCircle2 className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) return "Aujourd'hui";
    if (days === 1) return "Hier";
    if (days < 7) return `Il y a ${days} jours`;
    return date.toLocaleDateString('fr-FR');
  };

  const getDomainName = (domainId) => {
    const domain = domains.find(d => d.id === domainId);
    return domain ? domain.name : domainId;
  };

  const getJurisdictionLabel = (jurisdiction) => {
    const labels = {
      fr: "France",
      eu: "Europe",
      us: "États-Unis"
    };
    return labels[jurisdiction] || jurisdiction;
  };

  const getJurisdictionColor = (jurisdiction) => {
    const colors = {
      fr: "bg-blue-100 text-blue-800 border-blue-200",
      eu: "bg-yellow-100 text-yellow-800 border-yellow-200",
      us: "bg-red-100 text-red-800 border-red-200"
    };
    return colors[jurisdiction] || "bg-slate-100 text-slate-800";
  };

  const openDetailModal = (update) => {
    setSelectedUpdate(update);
    setDetailDialogOpen(true);
  };

  const closeDetailModal = () => {
    setDetailDialogOpen(false);
    setSelectedUpdate(null);
  };

  const getSortedUpdates = () => {
    const sorted = [...updates];
    switch (sortBy) {
      case "date_desc":
        return sorted.sort((a, b) => new Date(b.publication_date) - new Date(a.publication_date));
      case "date_asc":
        return sorted.sort((a, b) => new Date(a.publication_date) - new Date(b.publication_date));
      case "impact_high":
        const order = { high: 0, medium: 1, low: 2 };
        return sorted.sort((a, b) => (order[a.impact_level] ?? 3) - (order[b.impact_level] ?? 3));
      case "jurisdiction":
        return sorted.sort((a, b) => (a.jurisdiction || "").localeCompare(b.jurisdiction || ""));
      case "domain":
        return sorted.sort((a, b) => (a.domain || "").localeCompare(b.domain || ""));
      default:
        return sorted;
    }
  };

  return (
    <Layout user={user} cabinet={cabinet} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-serif text-3xl font-semibold tracking-tight text-blue-900">Veille Légale</h1>
            <p className="text-slate-600 mt-1">Surveillance automatisée des évolutions juridiques</p>
          </div>
          <div className="flex gap-2">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="legal-watch-sort-select"
            >
              <option value="date_desc">Plus récents d'abord</option>
              <option value="date_asc">Plus anciens d'abord</option>
              <option value="impact_high">Impact le plus élevé</option>
              <option value="jurisdiction">Par juridiction</option>
              <option value="domain">Par domaine</option>
            </select>
            <Dialog open={preferencesDialogOpen} onOpenChange={setPreferencesDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <Settings className="h-4 w-4 mr-2" />
                  Préférences
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Préférences de veille</DialogTitle>
                  <DialogDescription>
                    Sélectionnez les domaines juridiques que vous souhaitez suivre
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  {domains.map(domain => (
                    <div key={domain.id} className="flex items-start space-x-3">
                      <Checkbox
                        checked={preferences.tracked_domains.includes(domain.id)}
                        onCheckedChange={() => toggleDomain(domain.id)}
                        id={domain.id}
                      />
                      <div className="flex-1">
                        <label htmlFor={domain.id} className="text-sm font-medium cursor-pointer">
                          {domain.name}
                        </label>
                        <p className="text-xs text-slate-500">{domain.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <Button onClick={savePreferences} className="w-full bg-blue-600">
                  Enregistrer les préférences
                </Button>
              </DialogContent>
            </Dialog>
            
            <Button onClick={handleRefresh} disabled={loading} className="bg-blue-600">
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
          </div>
        </div>

        {/* Info banner */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-4">
            <div className="flex items-start space-x-3">
              <Bell className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-blue-900 mb-1">Veille automatisée quotidienne</h3>
                <p className="text-sm text-blue-800">
                  Les mises à jour sont vérifiées chaque jour. Vous recevrez une notification pour chaque nouvelle 
                  évolution dans vos domaines suivis. L'IA analyse automatiquement l'impact de chaque changement.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Filters Section - Updated Version 2.0 */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border-2 border-blue-300 shadow-md">
          <h2 className="text-xl font-bold text-blue-900 mb-4 flex items-center gap-2">
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            Filtres de Recherche
          </h2>
          
          {/* Jurisdiction filters */}
          <div className="mb-4">
            <h3 className="text-base font-semibold text-slate-800 mb-3 flex items-center gap-2">
              <Globe className="h-4 w-4 text-slate-400" /> Juridiction Géographique
            </h3>
            <div className="flex flex-wrap gap-2">
            <Button
              variant={selectedJurisdiction === "all" ? "default" : "outline"}
              onClick={() => handleJurisdictionFilter("all")}
              size="sm"
              className={`gap-1.5 ${selectedJurisdiction === "all" ? "bg-blue-600" : ""}`}
            >
              <Globe className="h-3.5 w-3.5" /> Toutes les juridictions
            </Button>
            <Button
              variant={selectedJurisdiction === "fr" ? "default" : "outline"}
              onClick={() => handleJurisdictionFilter("fr")}
              size="sm"
              className={selectedJurisdiction === "fr" ? "bg-blue-600" : ""}
            >
              France
            </Button>
            <Button
              variant={selectedJurisdiction === "eu" ? "default" : "outline"}
              onClick={() => handleJurisdictionFilter("eu")}
              size="sm"
              className={selectedJurisdiction === "eu" ? "bg-blue-600" : ""}
            >
              Europe
            </Button>
            <Button
              variant={selectedJurisdiction === "us" ? "default" : "outline"}
              onClick={() => handleJurisdictionFilter("us")}
              size="sm"
              className={selectedJurisdiction === "us" ? "bg-blue-600" : ""}
            >
              États-Unis
            </Button>
            </div>
          </div>
          
          {/* Domain filters */}
          <div className="mt-6">
            <h3 className="text-base font-semibold text-slate-800 mb-3 flex items-center gap-2">
              <Scale className="h-4 w-4 text-slate-400" /> Domaine Juridique
            </h3>
            <div className="flex flex-wrap gap-2">
            <Button
              variant={selectedDomain === "all" ? "default" : "outline"}
              onClick={() => handleDomainFilter("all")}
              size="sm"
              className={selectedDomain === "all" ? "bg-blue-600" : ""}
            >
              Tous les domaines
            </Button>
            {domains.map(domain => (
              <Button
                key={domain.id}
                variant={selectedDomain === domain.id ? "default" : "outline"}
                onClick={() => handleDomainFilter(domain.id)}
                size="sm"
                className={selectedDomain === domain.id ? "bg-blue-600" : ""}
              >
                {domain.name}
              </Button>
            ))}
            </div>
          </div>
        </div>
        {/* End Filters Section */}

        {/* Updates list */}
        <div className="space-y-4">
          {loading ? (
            <Card>
              <CardContent className="py-12 text-center text-slate-500">
                <RefreshCw className="h-12 w-12 mx-auto mb-3 animate-spin opacity-50" />
                <p>Chargement des mises à jour...</p>
              </CardContent>
            </Card>
          ) : updates.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-slate-400">
                <Bell className="h-16 w-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg">Aucune mise à jour disponible</p>
                <p className="text-sm mt-2">Configurez vos préférences pour suivre des domaines juridiques</p>
              </CardContent>
            </Card>
          ) : (
            getSortedUpdates().map((update) => (
              <Card 
                key={update.id} 
                className="hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => openDetailModal(update)}
              >
                <CardHeader>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex gap-2">
                      <Badge variant="outline" className="text-xs">
                        {getDomainName(update.domain)}
                      </Badge>
                      <Badge className={`text-xs border ${getJurisdictionColor(update.jurisdiction)}`}>
                        {getJurisdictionLabel(update.jurisdiction)}
                      </Badge>
                    </div>
                    <span className="text-xs text-slate-500">{formatDate(update.publication_date)}</span>
                  </div>
                  <CardTitle className="text-xl">{update.title}</CardTitle>
                  <CardDescription className="flex items-center gap-2 mt-2">
                    <span className="text-xs">{update.source}</span>
                    {update.reference && (
                      <>
                        <span>•</span>
                        <span className="text-xs font-mono">{update.reference}</span>
                      </>
                    )}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* AI Summary */}
                  {update.summary && (
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <p className="text-sm font-semibold text-slate-700 mb-2 flex items-center">
                        <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded mr-2">
                          Résumé IA
                        </span>
                      </p>
                      <p className="text-sm text-slate-700">{update.summary}</p>
                    </div>
                  )}

                  {/* Impact Analysis */}
                  {update.impact_level && (
                    <div className="flex items-start space-x-3">
                      <div className={`p-2 rounded ${getImpactBadgeColor(update.impact_level)}`}>
                        {getImpactIcon(update.impact_level)}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-semibold mb-1">
                          Impact estimé : 
                          <span className="ml-2 capitalize">{update.impact_level === 'high' ? 'Élevé' : update.impact_level === 'medium' ? 'Moyen' : 'Faible'}</span>
                        </p>
                        {update.impact_description && (
                          <p className="text-sm text-slate-600">{update.impact_description}</p>
                        )}
                      </div>
                    </div>
                  )}

                  <Separator />

                  {/* Click hint - Enhanced visibility */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                    <p className="text-sm font-semibold text-blue-700 flex items-center justify-center gap-2">
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      Cliquez pour voir le texte juridique complet
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>

      {/* Modal détaillé pour voir le texte complet */}
      {selectedUpdate && (
        <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <div className="space-y-3">
                <div className="flex items-start justify-between gap-4">
                  <DialogTitle className="text-2xl font-bold text-blue-900 pr-8">
                    {selectedUpdate.title}
                  </DialogTitle>
                </div>
                
                <div className="flex items-center gap-3 flex-wrap">
                  <Badge variant="outline" className="text-sm">
                    {getDomainName(selectedUpdate.domain)}
                  </Badge>
                  <Badge className={`text-sm border ${getJurisdictionColor(selectedUpdate.jurisdiction)}`}>
                    {getJurisdictionLabel(selectedUpdate.jurisdiction)}
                  </Badge>
                  <span className="text-sm text-slate-500">{formatDate(selectedUpdate.publication_date)}</span>
                  <span className="text-sm text-slate-600">•</span>
                  <span className="text-sm text-slate-600">{selectedUpdate.source}</span>
                  {selectedUpdate.reference && (
                    <>
                      <span className="text-sm text-slate-600">•</span>
                      <span className="text-sm font-mono text-slate-700">{selectedUpdate.reference}</span>
                    </>
                  )}
                </div>
              </div>
            </DialogHeader>

            <div className="space-y-6 mt-6">
              {/* AI Summary */}
              {selectedUpdate.summary && (
                <div className="bg-blue-50 border border-blue-200 p-5 rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="inline-flex items-center gap-1.5 bg-blue-600 text-white text-xs font-bold px-3 py-1.5 rounded">
                      <TrendingUp className="h-3.5 w-3.5" /> RÉSUMÉ IA
                    </span>
                  </div>
                  <p className="text-base text-slate-800 leading-relaxed">{selectedUpdate.summary}</p>
                </div>
              )}

              {/* Impact Analysis */}
              {selectedUpdate.impact_level && (
                <div className={`${getImpactBadgeColor(selectedUpdate.impact_level)} border-2 p-5 rounded-lg`}>
                  <div className="flex items-start gap-3">
                    <div className="mt-1">
                      {getImpactIcon(selectedUpdate.impact_level)}
                    </div>
                    <div className="flex-1">
                      <p className="text-base font-bold mb-2">
                        Impact estimé : 
                        <span className="ml-2 capitalize">
                          {selectedUpdate.impact_level === 'high' ? '🔴 Élevé' : 
                           selectedUpdate.impact_level === 'medium' ? '🟠 Moyen' : 
                           '🟢 Faible'}
                        </span>
                      </p>
                      {selectedUpdate.impact_description && (
                        <p className="text-base leading-relaxed">{selectedUpdate.impact_description}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <Separator />

              {/* Full Text */}
              {selectedUpdate.full_text && (
                <div className="bg-slate-50 border border-slate-200 p-5 rounded-lg">
                  <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Texte Complet
                  </h3>
                  <div className="prose prose-sm max-w-none">
                    <p className="text-base text-slate-700 leading-relaxed whitespace-pre-line">
                      {selectedUpdate.full_text}
                    </p>
                  </div>
                </div>
              )}

              {/* Tags */}
              {selectedUpdate.tags && selectedUpdate.tags.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-2">Tags :</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedUpdate.tags.map((tag, idx) => (
                      <Badge key={idx} variant="secondary" className="text-sm">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* External Link */}
              {selectedUpdate.url && (
                <div className="pt-4 border-t">
                  <Button 
                    className="w-full bg-blue-600 hover:bg-blue-700" 
                    asChild
                    onClick={(e) => e.stopPropagation()}
                  >
                    <a href={selectedUpdate.url} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Consulter la source officielle (Légifrance / EUR-Lex)
                    </a>
                  </Button>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </Layout>
  );
}
